import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, RotateCcw, Hand, Trash2, ListChecks, Sparkles, CalendarClock, Inbox } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PRIORITY_STYLES, fmtDueDate, isOverdue } from "@/lib/constants";

function DueBadge({ t }) {
  if (!t.due_date) return null;
  const over = isOverdue(t.due_date, t.status);
  return (
    <span
      data-testid={`mytask-due-${t.id}`}
      className={`text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded border ${
        over ? "bg-red-950/60 text-red-300 border-red-800/60" : "bg-black/30 text-gray-400 border-[#2E3242]"
      }`}
    >
      <CalendarClock className="w-2.5 h-2.5" />
      {over ? "Overdue " : "Due "}
      {fmtDueDate(t.due_date)}
    </span>
  );
}

function TaskRow({ t, mine, onUpdate, onDelete }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      data-testid={`mytask-card-${t.id}`}
      className="rounded-xl border border-[#2E3242] bg-[#1E2029] p-4 hover:border-red-900/40 transition-colors"
    >
      <div className="flex items-start justify-between gap-3">
        <p
          className={`text-sm leading-snug flex-1 ${
            t.status === "done" ? "line-through text-gray-500" : "text-gray-100"
          }`}
        >
          {t.title}
        </p>
        <button
          data-testid={`mytask-delete-${t.id}`}
          onClick={() => onDelete(t.id)}
          className="text-gray-600 hover:text-red-400 shrink-0"
          title="Delete task"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
        <span
          className={`text-[10px] font-medium px-1.5 py-0.5 rounded border capitalize ${PRIORITY_STYLES[t.priority]}`}
        >
          {t.priority}
        </span>
        {t.source === "ai" && (
          <span className="text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-950/50 text-red-300 border border-red-900/40">
            <Sparkles className="w-2.5 h-2.5" /> AI
          </span>
        )}
        <DueBadge t={t} />
        {t.created_by && (
          <span className="text-[10px] text-gray-600">from {t.created_by}</span>
        )}
      </div>

      <div className="flex items-center gap-2 mt-3">
        {t.status !== "done" ? (
          <>
            <button
              data-testid={`mytask-done-${t.id}`}
              onClick={() => onUpdate(t.id, { status: "done" })}
              className="text-[11px] flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-950/50 text-emerald-300 border border-emerald-900/40 hover:bg-emerald-900/50"
            >
              <CheckCircle2 className="w-3 h-3" /> Mark done
            </button>
            <button
              data-testid={`mytask-release-${t.id}`}
              onClick={() => onUpdate(t.id, { unclaim: true })}
              className="text-[11px] flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-[#262936] text-gray-400 border border-[#2E3242] hover:text-gray-200"
            >
              <Hand className="w-3 h-3" /> Release
            </button>
          </>
        ) : (
          <button
            data-testid={`mytask-reopen-${t.id}`}
            onClick={() => onUpdate(t.id, { status: "in-progress" })}
            className="text-[11px] flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-[#262936] text-gray-400 border border-[#2E3242] hover:text-gray-200"
          >
            <RotateCcw className="w-3 h-3" /> Reopen
          </button>
        )}
      </div>
    </motion.div>
  );
}

export default function MyTasks({ tasks, onUpdate, onDelete }) {
  const { user } = useAuth();
  const mine = tasks.filter((t) => t.assignee_id === user?.id);
  const active = mine.filter((t) => t.status !== "done");
  const done = mine.filter((t) => t.status === "done");
  const assigned = tasks.filter(
    (t) => t.assigned_to_id === user?.id && t.status !== "done" && t.assignee_id !== user?.id
  );

  const empty = mine.length === 0 && assigned.length === 0;

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-[#16181F]" data-testid="my-tasks">
      <div className="max-w-2xl w-full mx-auto flex-1 overflow-y-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-3 mb-1">
          <span className="w-10 h-10 rounded-xl ruby-gradient flex items-center justify-center">
            <ListChecks className="w-5 h-5 text-white" />
          </span>
          <div>
            <h2 className="font-display font-bold text-lg text-gray-100">My To-Do List</h2>
            <p className="text-sm text-gray-500">
              Assigned to you & tasks you've claimed · {active.length + assigned.length} to do
            </p>
          </div>
        </div>

        {empty && (
          <div className="mt-16 flex flex-col items-center text-center px-4">
            <div className="w-14 h-14 rounded-2xl bg-[#1E2029] border border-[#2E3242] flex items-center justify-center mb-4">
              <Hand className="w-6 h-6 text-red-400" />
            </div>
            <p className="font-display font-bold text-gray-100">Your list is empty</p>
            <p className="text-sm text-gray-500 mt-1 max-w-sm">
              Head to the Task Board and hit{" "}
              <span className="text-blue-300 font-medium">"I'm on it"</span> on any task — or wait for
              a teammate to assign one to you. It'll show up here.
            </p>
          </div>
        )}

        {assigned.length > 0 && (
          <div className="mt-6">
            <p className="text-[11px] uppercase tracking-wider text-violet-300/80 mb-3 flex items-center gap-1.5">
              <Inbox className="w-3.5 h-3.5" /> Assigned to you ({assigned.length})
            </p>
            <div className="space-y-2.5">
              <AnimatePresence initial={false}>
                {assigned.map((t) => (
                  <motion.div
                    key={t.id}
                    layout
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.97 }}
                    data-testid={`mytask-assigned-${t.id}`}
                    className={`rounded-xl border p-4 transition-colors ${
                      isOverdue(t.due_date, t.status)
                        ? "border-red-800/60 bg-red-950/20"
                        : "border-violet-900/40 bg-[#1E2029]"
                    }`}
                  >
                    <p className="text-sm leading-snug text-gray-100">{t.title}</p>
                    <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
                      <span
                        className={`text-[10px] font-medium px-1.5 py-0.5 rounded border capitalize ${PRIORITY_STYLES[t.priority]}`}
                      >
                        {t.priority}
                      </span>
                      <DueBadge t={t} />
                      {t.created_by && (
                        <span className="text-[10px] text-gray-600">assigned by {t.created_by}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        data-testid={`mytask-accept-${t.id}`}
                        onClick={() => onUpdate(t.id, { claim: true })}
                        className="text-[11px] flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-blue-950/50 text-blue-300 border border-blue-900/40 hover:bg-blue-900/50"
                      >
                        <Hand className="w-3 h-3" /> I'm on it
                      </button>
                      <button
                        data-testid={`mytask-accept-done-${t.id}`}
                        onClick={() => onUpdate(t.id, { status: "done" })}
                        className="text-[11px] flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-950/50 text-emerald-300 border border-emerald-900/40 hover:bg-emerald-900/50"
                      >
                        <CheckCircle2 className="w-3 h-3" /> Mark done
                      </button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        )}

        {active.length > 0 && (
          <div className="mt-8">
            <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-3">
              In progress ({active.length})
            </p>
            <div className="space-y-2.5">
              <AnimatePresence initial={false}>
                {active.map((t) => (
                  <TaskRow key={t.id} t={t} mine onUpdate={onUpdate} onDelete={onDelete} />
                ))}
              </AnimatePresence>
            </div>
          </div>
        )}

        {done.length > 0 && (
          <div className="mt-8">
            <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-3">
              Completed ({done.length})
            </p>
            <div className="space-y-2.5">
              <AnimatePresence initial={false}>
                {done.map((t) => (
                  <TaskRow key={t.id} t={t} mine onUpdate={onUpdate} onDelete={onDelete} />
                ))}
              </AnimatePresence>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
